import { date } from "common";

const { startOfWeek, addDays, startOfMonth, endOfMonth, addYears, truncateTime } = date;

/**
 * Calendar calculation service - pure functions for date manipulation
 * No state, just calculations
 */
export class CalendarService {
    /**
     * Get all weeks for a given month
     */
    static weeksFor(day: Date): Date[] {
        const res: Date[] = [];
        let start = startOfWeek(startOfMonth(day));
        while (start <= endOfMonth(day)) {
            res.push(addDays(start, day.getDay()));
            start = addDays(start, 7);
        }
        return res;
    }

    /**
     * Get list of years within a date range
     */
    static years(firstDate?: Date, lastDate?: Date): number[] {
        const res: number[] = [];
        const start = firstDate ?? date.today();
        const end = lastDate ?? start;

        let current = start;
        while (current <= end) {
            res.push(current.getFullYear());
            current = addYears(current, 1);
        }
        return res;
    }

    /**
     * Check if a month falls within the valid date range
     */
    static monthEnabled(dt: Date, firstDate?: Date, lastDate?: Date, isEnabled: boolean = true): boolean {
        if (!isEnabled) return false;

        const monthStart = truncateTime(startOfMonth(dt));
        const monthEnd = truncateTime(endOfMonth(dt));

        if (firstDate && monthEnd.getTime() < firstDate.getTime()) {
            return false;
        }
        if (lastDate && monthStart.getTime() > lastDate.getTime()) {
            return false;
        }

        return true;
    }

    /**
     * Calculate date range for navigation (2 years before/after focus date)
     */
    static getDateRange(focusDate: Date): { firstDate: Date; lastDate: Date } {
        return {
            firstDate: addYears(focusDate, -2),
            lastDate: addYears(focusDate, 2)
        };
    }

    /**
     * Format date for API calls (M/d/yyyy)
     */
    static formatDateForApi(dt: Date): string {
        return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
    }
}
